import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { nodeDetailQueryKeys } from 'api/data/hooks/nodeDetailQueryKeys';
import { useUpdateProjectNode } from 'api/data/hooks/useUpdateProjectNode';
import { normalizePropertyValue } from 'api/data/projects/normalizePropertyValue';
import { type ApiNodeDetail, createPlaceholderApiNodeProperty, toPropertyValue } from 'api/data/projects/types';
import type { PropertyValue } from 'state/data/dataState';
import { SNACKBAR_STORE } from 'state/snackbar/snackbarStore';

interface UpdateNodeDetailPropertyArgs {
  propertyId: string;
  technicalName: string;
  newValue: PropertyValue;
}

/**
 * Decoupled property update for the node details panel.
 *
 * Uses {@link useUpdateProjectNode} (PUT `.../nodes/{nodeId}` with a technicalName→value
 * map) and writes the value into the `useNodeDetail` React Query cache optimistically,
 * rolling it back if the request fails. Tree labels sync separately via SignalR
 * `DataNodeUpdated`.
 */
export function useUpdateNodeDetailProperty(
  projectId: string,
  templateNodeId: string,
  dataNodeId: string,
  proposalId?: string
) {
  const queryClient = useQueryClient();
  const { mutate } = useUpdateProjectNode();

  const detailQueryKey = useMemo(
    () => nodeDetailQueryKeys.detail(projectId, dataNodeId, proposalId, templateNodeId),
    [projectId, dataNodeId, proposalId, templateNodeId]
  );

  const writeCachedValue = useCallback(
    (technicalName: string, value: PropertyValue) => {
      queryClient.setQueryData<ApiNodeDetail>(detailQueryKey, (old) => {
        if (!old) return old;
        const existing = old.properties[technicalName] ?? createPlaceholderApiNodeProperty();
        return {
          ...old,
          properties: {
            ...old.properties,
            [technicalName]: { ...existing, value }
          }
        };
      });
    },
    [queryClient, detailQueryKey]
  );

  return useCallback(
    (args: UpdateNodeDetailPropertyArgs) => {
      const { technicalName, newValue } = args;
      const normalizedValue = normalizePropertyValue(newValue);

      const cachedProperty = queryClient.getQueryData<ApiNodeDetail>(detailQueryKey)?.properties[technicalName];
      const previousValue = toPropertyValue(cachedProperty?.value ?? null);

      // Nothing to save if the value already matches the cache — a focused field can be edited back
      // to a value a live patch already installed. Compared as strings (as elsewhere) so '99' === 99.
      if (String(normalizedValue ?? '') === String(previousValue ?? '')) return;

      // Write first so the field shows the saved value immediately; NodeDetailsProperty also
      // resyncs its local edit state from this value, which is what reverts the input on failure.
      writeCachedValue(technicalName, normalizedValue);

      mutate(
        {
          projectId,
          nodeId: dataNodeId,
          templateNodeId,
          properties: { [technicalName]: normalizedValue },
          proposalId
        },
        {
          onError: () => {
            const currentValue = toPropertyValue(
              queryClient.getQueryData<ApiNodeDetail>(detailQueryKey)?.properties[technicalName]?.value ?? null
            );
            // Only revert if nothing else (e.g. another user's live-synced edit) has overwritten
            // the optimistic value in the meantime — otherwise this would clobber a newer value.
            if (String(currentValue ?? '') === String(normalizedValue ?? '')) {
              writeCachedValue(technicalName, previousValue);
            }
            SNACKBAR_STORE.getState().showSnackbar('Failed to save changes. Please try again.', 'error', true);
          }
        }
      );
    },
    [mutate, queryClient, detailQueryKey, writeCachedValue, projectId, templateNodeId, dataNodeId, proposalId]
  );
}
